export type ProductFitType =
  | "apparel"
  | "shoe"
  | "bag"
  | "hat"
  | "sunglasses"
  | "earring"
  | "necklace"
  | "bracelet"
  | "ring"
  | "belt"
  | "watch"
  | "accessory"
  | "unknown";

export type ProductMeasurementType = "body" | "foot" | "head" | "face" | "wrist" | "body-basic";
export type ProductGender = "male" | "female" | "unisex";

interface ProductFitInput {
  productFitType?: string | null;
  productGender?: string | null;
  productTitle?: string | null;
  productCategory?: string | null;
  productSubcategory?: string | null;
  productDescription?: string | null;
  productType?: string | null;
  productTags?: string[] | string | null;
}

const FIT_TYPES: ProductFitType[] = [
  "apparel",
  "shoe",
  "bag",
  "hat",
  "sunglasses",
  "earring",
  "necklace",
  "bracelet",
  "ring",
  "belt",
  "watch",
  "accessory",
  "unknown",
];

const FIT_KEYWORDS: Array<{ fitType: ProductFitType; pattern: RegExp }> = [
  {
    fitType: "shoe",
    pattern: /\b(shoe|shoes|sneaker|sneakers|boot|boots|heel|heels|loafer|loafers|mule|mules|sandal|sandals|trainer|trainers|slipper|slippers|stiletto|stilettos|pump|pumps|oxford|derby|derbies|wedge|espadrille|clog|footwear|foot length|footlength|footLengthCm|shoeEU|shoeUS|shoeUK)\b/i,
  },
  {
    fitType: "hat",
    pattern: /\b(hat|hats|cap|caps|beanie|beanies|bucket hat|fedora|snapback|baseball cap|trucker hat|beret|panama|headband|visor|bonnet|headwear|head circumference|headcircumference)\b/i,
  },
  {
    fitType: "sunglasses",
    pattern: /\b(sunglass|sunglasses|eyewear|eyeglasses|glasses|spectacles|optical|goggles|frames|aviator|wayfarer|lens|bridge width|bridgewidth|temple length|templelength|lens width|lenswidth|pupillary|pd)\b/i,
  },
  {
    fitType: "bag",
    pattern: /\b(handbag|handbags|bag|bags|tote|totes|shoulder bag|hobo bag|crossbody|clutch|satchel|backpack|backpacks|weekender|duffle|duffel|wallet|wallets|purse|purses|briefcase|belt bag|fanny pack|luggage|suitcase|suitcases|small leather goods|leather goods)\b/i,
  },
  {
    fitType: "apparel",
    pattern: /\b(shirt|shirts|t-shirt|tee|top|tops|dress|dresses|jacket|jackets|coat|coats|pants|trousers|jeans|skirt|skirts|shorts|suit|suits|tuxedo|tuxedos|blazer|blazers|hoodie|hoodies|sweater|sweaters|cardigan|cardigans|apparel|clothing|garment|garments)\b/i,
  },
  {
    fitType: "belt",
    pattern: /\b(belt|belts|waist belt)\b/i,
  },
  {
    fitType: "necklace",
    pattern: /\b(necklace|necklaces|pendant|pendants|chain|chains|choker|chokers)\b/i,
  },
  {
    fitType: "earring",
    pattern: /\b(earring|earrings|ear cuff|ear cuffs|stud|studs|hoop|hoops|drop earring|drop earrings)\b/i,
  },
  {
    fitType: "bracelet",
    pattern: /\b(bracelet|bracelets|bangle|bangles|cuff|cuffs)\b/i,
  },
  {
    fitType: "ring",
    pattern: /\b(ring|rings|band|bands)\b/i,
  },
  {
    fitType: "watch",
    pattern: /\b(watch|watches|timepiece|timepieces)\b/i,
  },
  {
    fitType: "accessory",
    pattern: /\b(accessory|accessories|jewelry|jewellery|brooch|brooches|scarf|scarves|glove|gloves)\b/i,
  },
];

const APPAREL_PATTERN =
  /\b(shirt|shirts|t-shirt|tee|top|tops|dress|dresses|jacket|jackets|coat|coats|pants|trousers|jeans|skirt|skirts|shorts|suit|suits|tuxedo|tuxedos|blazer|blazers|hoodie|hoodies|sweater|sweaters|cardigan|cardigans|apparel|clothing|garment|garments|uniform|scrub|scrubs)\b/i;

const BODY_BASIC_FIT_TYPES = new Set<ProductFitType>([
  "bag",
  "belt",
  "earring",
  "necklace",
  "bracelet",
  "ring",
  "watch",
  "accessory",
]);

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/[_\s-]+/g, "-");
}

export function normalizeProductFitType(value?: string | null): ProductFitType | null {
  if (!value) return null;
  const normalized = normalize(value);
  const matched = FIT_TYPES.find((item) => item === normalized);
  return matched ?? null;
}

export function normalizeProductGender(value?: string | null): ProductGender | null {
  if (!value) return null;
  const normalized = normalize(value);
  if (["female", "women", "woman", "womens", "women-s", "ladies", "girl", "girls"].includes(normalized)) return "female";
  if (["male", "men", "man", "mens", "men-s", "boy", "boys"].includes(normalized)) return "male";
  if (["unisex", "all", "all-gender", "gender-neutral"].includes(normalized)) return "unisex";
  return null;
}

function hasBustSignal(value: unknown, depth = 0): boolean {
  if (value == null || depth > 5) return false;
  if (typeof value === "string") return /\bbust\b|chest\s*\/\s*bust|chest\s+or\s+bust/i.test(value);
  if (Array.isArray(value)) return value.some((item) => hasBustSignal(item, depth + 1));
  if (typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  for (const [key, item] of Object.entries(obj)) {
    if (/\bbust\b|chest\s*\/\s*bust|chest\s+or\s+bust/i.test(key)) return true;
    if (["requiredFields", "headers", "sections", "rows", "fields"].includes(key) && hasBustSignal(item, depth + 1)) {
      return true;
    }
  }
  return false;
}

function productTagsToText(productTags?: string[] | string | null): string {
  if (!productTags) return "";
  if (Array.isArray(productTags)) return productTags.join(" ");
  return productTags;
}

export function detectProductFitType(input: ProductFitInput): ProductFitType {
  const explicit = normalizeProductFitType(input.productFitType);
  const searchText = [
    input.productCategory,
    input.productSubcategory,
    input.productType,
    productTagsToText(input.productTags),
    input.productTitle,
    input.productDescription,
  ]
    .filter(Boolean)
    .join(" ");

  if (explicit && explicit !== "unknown") {
    if (BODY_BASIC_FIT_TYPES.has(explicit) && APPAREL_PATTERN.test(searchText)) {
      return "apparel";
    }
    return explicit;
  }

  for (const rule of FIT_KEYWORDS) {
    if (rule.pattern.test(searchText)) return rule.fitType;
  }

  return explicit ?? "unknown";
}

export function detectProductGender(input: ProductFitInput, sizeGuide?: unknown): ProductGender | null {
  const explicit = normalizeProductGender(input.productGender);
  if (explicit) return explicit;
  if (hasBustSignal(sizeGuide)) return "female";

  const searchText = [
    input.productCategory,
    input.productSubcategory,
    input.productType,
    productTagsToText(input.productTags),
    input.productTitle,
    input.productDescription,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/\b(unisex|gender neutral|all gender|all-gender)\b/i.test(searchText)) return "unisex";
  const hasMale = /\b(men|mens|men's|male|man|boys?)\b/i.test(searchText);
  const hasFemale = /\b(women|womens|women's|female|woman|ladies|girls?)\b/i.test(searchText);
  if (hasMale && !hasFemale) return "male";
  if (hasFemale && !hasMale) return "female";
  if (hasFemale && hasMale) return "unisex";

  const dressShirt = /\bdress\s+shirt\b/i.test(searchText);
  if (/\b(skirt|skirts|blouse|blouses|bra|bras)\b/i.test(searchText)) return "female";
  if (!dressShirt && /\b(dress|dresses)\b/i.test(searchText)) return "female";

  return null;
}

export function productFitTypeToMeasurementType(fitType: ProductFitType): ProductMeasurementType {
  if (fitType === "shoe") return "foot";
  if (fitType === "hat") return "head";
  if (fitType === "sunglasses") return "face";
  if (fitType === "bracelet") return "body-basic";
  if (
    fitType === "bag" ||
    fitType === "belt" ||
    fitType === "earring" ||
    fitType === "necklace" ||
    fitType === "ring" ||
    fitType === "watch" ||
    fitType === "accessory"
  ) {
    return "body-basic";
  }
  return "body";
}
